import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { PhotoUpload, EvidenceThumb } from "./Evidence";
import { Wrench } from "@phosphor-icons/react";

const OUTCOMES = [["fixed", "Fixed", "border-emerald-500 bg-emerald-50"], ["partly_fixed", "Partly fixed", "border-amber-500 bg-amber-50"], ["still_faulty", "Still faulty", "border-red-500 bg-red-50"]];
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

// "Record repair" — anyone records work THEY performed. Server stamps who + when; never closes other faults or job cards.
export function RecordRepairButton({ assetId, faultId, jobId, checkId, source, label = "Record repair", onDone, className = "", testid = "record-repair" }) {
  const [open, setOpen] = useState(false);
  return (<>
    <Button data-testid={testid} variant="outline" onClick={() => setOpen(true)} className={`font-semibold gap-1 ${className}`}><Wrench size={15} weight="bold" />{label}</Button>
    {open && <RecordRepairDialog assetId={assetId} faultId={faultId} jobId={jobId} checkId={checkId} source={source} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone?.(); }} />}
  </>);
}

export function RecordRepairDialog({ assetId, faultId, jobId, checkId, source, onClose, onDone }) {
  const [key] = useState(uid);
  const [work, setWork] = useState("");
  const [parts, setParts] = useState("");
  const [photos, setPhotos] = useState([]);
  const [outcome, setOutcome] = useState("");
  const [busy, setBusy] = useState(false);
  const [faults, setFaults] = useState([]);
  const [fid, setFid] = useState(faultId || "");
  useEffect(() => { if (!faultId) api.get(`/repairs/open-faults/${assetId}`).then((r) => setFaults(r.data)).catch(() => {}); }, [assetId, faultId]);
  const submit = async () => {
    if (busy) return; setBusy(true);
    try {
      const r = await api.post("/repairs", { asset_id: assetId, fault_id: fid || undefined, job_id: jobId || undefined, check_id: checkId || undefined,
        source, work_performed: work, outcome, photos, idempotency_key: key,
        parts_used: parts.split("\n").map((s) => s.trim()).filter(Boolean).map((description) => ({ description })) });
      const d = r.data;
      toast.success(d.fault_resolved ? "Repair recorded — fault resolved" : d.needs_signoff ? "Repair recorded — restriction stays until authorised sign-off" : "Repair recorded");
      onDone?.(d);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not record repair"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="record-repair-dialog">
        <DialogHeader><DialogTitle className="font-display">Record repair</DialogTitle>
          <DialogDescription>Log work you personally carried out. Your name and the time are recorded automatically.</DialogDescription></DialogHeader>
        {!faultId && (
          <select data-testid="repair-fault-select" value={fid} onChange={(e) => setFid(e.target.value)} className="h-10 border-2 border-slate-300 rounded px-2 text-sm">
            <option value="">No fault — general repair/maintenance</option>
            {faults.map((f) => <option key={f.id} value={f.id}>{f.title || f.description}</option>)}
          </select>
        )}
        <Textarea data-testid="repair-work" value={work} onChange={(e) => setWork(e.target.value)} placeholder="Work performed (required)" className="min-h-[80px]" />
        <Textarea data-testid="repair-parts" value={parts} onChange={(e) => setParts(e.target.value)} placeholder="Parts or materials used — one per line (optional)" className="min-h-[50px]" />
        <div className="flex items-center gap-2 flex-wrap">
          <PhotoUpload category="repair" label="Add photo" onUploaded={(f) => setPhotos((p) => [...p, f.id])} />
          {photos.map((p) => <EvidenceThumb key={p} fileId={p} />)}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {OUTCOMES.map(([k, l, cls]) => (
            <button key={k} data-testid={`repair-outcome-${k}`} onClick={() => setOutcome(k)} className={`h-11 rounded border-2 text-sm font-semibold ${outcome === k ? cls : "border-slate-200"}`}>{l}</button>
          ))}
        </div>
        <Button data-testid="repair-submit" disabled={busy || !work.trim() || !outcome} onClick={submit} className="bg-emerald-700 hover:bg-emerald-800 font-semibold">{busy ? "Saving…" : "Save repair"}</Button>
      </DialogContent>
    </Dialog>
  );
}

// Maintenance actions on one fault: repair now, new job card, add to existing job card, order parts.
export function FaultMaintenanceActions({ fault, onDone }) {
  const [mode, setMode] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [f, setF] = useState({ priority: "medium", due_date: "", job_id: "", parts: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => { if (mode === "add") api.get(`/maintenance/jobs?asset_id=${fault.asset_id}`).then((r) => setJobs((r.data || []).filter((j) => j.status !== "completed"))).catch(() => {}); }, [mode, fault.asset_id]);
  const run = async (fn, ok) => {
    if (busy) return; setBusy(true);
    try { await fn(); toast.success(ok); setMode(null); onDone?.(); } catch (e) { const d = e.response?.data?.detail; toast.error(d?.message || d || "Failed"); } finally { setBusy(false); }
  };
  const create = () => run(() => api.post("/maintenance/jobs", { asset_id: fault.asset_id, source_fault_id: fault.id, title: fault.title || fault.description, priority: f.priority, due_date: f.due_date || null }), "Job card created");
  const add = () => run(() => api.post(`/maintenance/jobs/${f.job_id}/add-fault`, { fault_id: fault.id }), "Added to job card");
  const order = () => run(() => api.post(`/repairs/faults/${fault.id}/parts`, { parts: f.parts.split("\n").map((s) => s.trim()).filter(Boolean).map((description) => ({ description })) }), "Parts added — job card awaiting parts");
  const sel = "h-9 border-2 border-slate-300 rounded px-2 text-sm";
  return (
    <div className="flex flex-col gap-2" data-testid={`fault-actions-${fault.id}`}>
      <div className="flex flex-wrap gap-1.5">
        <RecordRepairButton assetId={fault.asset_id} faultId={fault.id} source="repair_now" label="Repair now" testid={`repair-now-${fault.id}`} onDone={onDone} className="h-8 text-xs" />
        <Button data-testid={`fa-new-job-${fault.id}`} variant="outline" onClick={() => setMode(mode === "new" ? null : "new")} className="h-8 text-xs font-semibold">New job card</Button>
        <Button data-testid={`fa-add-job-${fault.id}`} variant="outline" onClick={() => setMode(mode === "add" ? null : "add")} className="h-8 text-xs font-semibold">Add to job card</Button>
        <Button data-testid={`fa-parts-${fault.id}`} variant="outline" onClick={() => setMode(mode === "parts" ? null : "parts")} className="h-8 text-xs font-semibold">Order parts</Button>
      </div>
      {mode === "new" && <div className="flex flex-wrap gap-2 items-center">
        <select data-testid="fa-priority" className={sel} value={f.priority} onChange={(e) => set("priority", e.target.value)}>{["urgent", "high", "medium", "low"].map((p) => <option key={p}>{p}</option>)}</select>
        <Input data-testid="fa-due" type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className="h-9 w-40" />
        <Button data-testid="fa-create" disabled={busy} onClick={create} className="h-9 text-xs">Create job card</Button></div>}
      {mode === "add" && <div className="flex flex-wrap gap-2 items-center">
        <select data-testid="fa-job-select" className={sel} value={f.job_id} onChange={(e) => set("job_id", e.target.value)}><option value="">Open job card on this asset…</option>{jobs.map((j) => <option key={j.id} value={j.id}>{j.job_number} · {j.title}</option>)}</select>
        <Button data-testid="fa-add" disabled={busy || !f.job_id} onClick={add} className="h-9 text-xs">Add</Button></div>}
      {mode === "parts" && <div className="flex flex-col gap-2">
        <Textarea data-testid="fa-parts-text" value={f.parts} onChange={(e) => set("parts", e.target.value)} placeholder="Parts required — one per line" className="min-h-[50px]" />
        <Button data-testid="fa-parts-submit" disabled={busy || !f.parts.trim()} onClick={order} className="h-9 text-xs self-start">Add parts to job card</Button></div>}
    </div>
  );
}
